import React, { useMemo, useState } from 'react';
import { X, UserPlus, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { whatsappApi } from '../../../services/whatsappApi';
import { TIMEZONE_OPTIONS, phoneToTimezone } from './phoneTimezone';

interface AddRecipientModalProps {
  open: boolean;
  clientId: string;
  channelId: string;
  onClose: () => void;
  onAdded: () => void;
}

export const AddRecipientModal: React.FC<AddRecipientModalProps> = ({
  open,
  clientId,
  channelId,
  onClose,
  onAdded,
}) => {
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [optInConfirmed, setOptInConfirmed] = useState(false);
  const [preferredTime, setPreferredTime] = useState('');
  const [timezone, setTimezone] = useState(browserTz);
  // Track whether the user has manually picked a timezone — once they do,
  // we stop auto-overwriting it as they keep editing the phone number.
  const [tzTouched, setTzTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Hooks must run unconditionally on every render — keep these ABOVE the
  // early-return guard below. Inject the current timezone into the dropdown
  // options if it's not in the curated list, so the <select> renders.
  const tzOptions = useMemo(() => {
    if (TIMEZONE_OPTIONS.some((o) => o.value === timezone)) return TIMEZONE_OPTIONS;
    return [...TIMEZONE_OPTIONS, { value: timezone, label: timezone, region: 'Other' }];
  }, [timezone]);

  const tzGroups = useMemo(() => {
    const groups: Record<string, typeof TIMEZONE_OPTIONS> = {};
    for (const o of tzOptions) {
      (groups[o.region] = groups[o.region] || []).push(o);
    }
    return groups;
  }, [tzOptions]);

  if (!open) return null;

  const reset = () => {
    setPhone('');
    setName('');
    setTagsInput('');
    setOptInConfirmed(false);
    setPreferredTime('');
    setTimezone(browserTz);
    setTzTouched(false);
    setSubmitting(false);
  };

  const onPhoneChange = (value: string) => {
    setPhone(value);
    if (tzTouched) return;
    const inferred = phoneToTimezone(value);
    if (inferred) setTimezone(inferred);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim()) {
      toast.error('Phone number required');
      return;
    }
    if (!optInConfirmed) {
      toast.error('You must confirm explicit opt-in (Meta policy requirement)');
      return;
    }

    setSubmitting(true);
    try {
      const tags = tagsInput
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      await whatsappApi.createRecipient(clientId, channelId, {
        phone_e164: phone.trim(),
        display_name: name.trim() || undefined,
        tags: tags.length ? tags : undefined,
        opt_in_confirmed: true,
        preferred_time_local: preferredTime.trim() || undefined,
        timezone: timezone || undefined,
      });
      toast.success('Recipient added');
      reset();
      onAdded();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to add recipient';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="font-medium text-gray-900">Add WhatsApp Recipient</h3>
            <p className="text-xs text-gray-500 mt-0.5">They'll be able to chat with the bot.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            disabled={submitting}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Phone (E.164) <span className="text-red-500">*</span>
            </label>
            <input
              value={phone}
              onChange={(e) => onPhoneChange(e.target.value)}
              placeholder="+18761234567"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
              required
            />
            <p className="text-xs text-gray-400 mt-1">Include country code with +.</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Display name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Tags (comma-separated)
            </label>
            <input
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="cohort-a, diabetic"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Preferred check-in time
              </label>
              <input
                type="time"
                value={preferredTime}
                onChange={(e) => setPreferredTime(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
              />
              <p className="text-xs text-gray-400 mt-1">For per-recipient schedules. Optional.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Timezone</label>
              <select
                value={timezone}
                onChange={(e) => {
                  setTimezone(e.target.value);
                  setTzTouched(true);
                }}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
              >
                {Object.entries(tzGroups).map(([region, opts]) => (
                  <optgroup key={region} label={region}>
                    {opts.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">
                {tzTouched ? 'Manually set.' : 'Auto-detected from phone.'}
              </p>
            </div>
          </div>

          <label className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 cursor-pointer">
            <input
              type="checkbox"
              checked={optInConfirmed}
              onChange={(e) => setOptInConfirmed(e.target.checked)}
              className="mt-0.5"
            />
            <span className="text-amber-800 leading-snug">
              I confirm this person has explicitly consented to receive WhatsApp messages
              from this business. (Required by Meta policy.)
            </span>
          </label>

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !optInConfirmed}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 whitespace-nowrap"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
              {submitting ? 'Adding…' : 'Add recipient'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddRecipientModal;
